/**
 * Media source registry: the storage ids a project can use, how each one is
 * treated by the rest of the app, and which Video.js media element plays it.
 */

export const MEDIA_SOURCES = Object.freeze({
    LOCAL: "Local Storage",
    CLOUD: "Cloud Storage",
    YOUTUBE: "YouTube",
    VIMEO: "Vimeo",
    HLS: "HLS Manifest",
    PROXY_RT: "Proxy RT",
    MUX: "Mux",
    DASH: "DASH Manifest",
    CLOUDFLARE: "Cloudflare Stream",
});

/** Played from a URL with no local file, so there is no audio extraction step. */
export const STREAMED_SOURCES = Object.freeze([
    MEDIA_SOURCES.YOUTUBE,
    MEDIA_SOURCES.VIMEO,
    MEDIA_SOURCES.HLS,
    MEDIA_SOURCES.MUX,
    MEDIA_SOURCES.DASH,
    MEDIA_SOURCES.CLOUDFLARE,
]);

/** Remote media that the waveform generator must not download and decode. */
const REMOTE_URL_SOURCES = new Set([...STREAMED_SOURCES, MEDIA_SOURCES.CLOUD]);

/** Adaptive or embedded streams whose audio cannot be sent to transcription. */
const NON_TRANSCRIBABLE_SOURCES = new Set([
    MEDIA_SOURCES.HLS,
    MEDIA_SOURCES.VIMEO,
    MEDIA_SOURCES.MUX,
    MEDIA_SOURCES.DASH,
    MEDIA_SOURCES.CLOUDFLARE,
]);

/** Streams whose import finishes when the player reports the manifest loaded. */
const MANIFEST_SOURCES = new Set([
    MEDIA_SOURCES.HLS,
    MEDIA_SOURCES.MUX,
    MEDIA_SOURCES.DASH,
]);

/** Played inside a third-party iframe, so CSS video filters do not apply. */
const EMBED_SOURCES = new Set([
    MEDIA_SOURCES.YOUTUBE,
    MEDIA_SOURCES.VIMEO,
    MEDIA_SOURCES.CLOUDFLARE,
]);

export function isStreamedSource(storage) {
    return STREAMED_SOURCES.includes(storage);
}

export function isRemoteUrlSource(storage) {
    return REMOTE_URL_SOURCES.has(storage);
}

export function isNonTranscribableSource(storage) {
    return NON_TRANSCRIBABLE_SOURCES.has(storage);
}

/** Local, cloud, YouTube, and Proxy RT can be sent to automatic transcription. */
export function canTranscribeMedia(media) {
    const hasMediaPath = Boolean(media?.path);
    const isStreamMedia = isNonTranscribableSource(media?.storage);
    return (hasMediaPath && !isStreamMedia) || media?.storage === MEDIA_SOURCES.PROXY_RT;
}

export function isManifestSource(storage) {
    return MANIFEST_SOURCES.has(storage);
}

export function isEmbedSource(storage) {
    return EMBED_SOURCES.has(storage);
}

/** @typedef {"file" | "hls" | "mux" | "dash" | "cloudflare" | "youtube" | "vimeo"} MediaKind */

/** Custom element tag for each media kind; `file` uses the native element. */
export const MEDIA_KIND_TAGS = Object.freeze({
    file: "video",
    hls: "hlsjs-video",
    mux: "mux-video",
    dash: "dash-video",
    cloudflare: "cloudflare-video",
    youtube: "youtube-video",
    vimeo: "vimeo-video",
});

/** Used when the URL itself does not identify the provider or format. */
const STORAGE_KINDS = {
    [MEDIA_SOURCES.HLS]: "hls",
    [MEDIA_SOURCES.MUX]: "mux",
    [MEDIA_SOURCES.DASH]: "dash",
    [MEDIA_SOURCES.CLOUDFLARE]: "cloudflare",
    [MEDIA_SOURCES.YOUTUBE]: "youtube",
    [MEDIA_SOURCES.VIMEO]: "vimeo",
};

const HLS_TYPES = ["application/x-mpegurl", "application/vnd.apple.mpegurl"];
const DASH_TYPES = ["application/dash+xml"];

function parseUrl(src) {
    try {
        return new URL(src);
    } catch {
        return null;
    }
}

function hostMatches(host, domain) {
    return host === domain || host.endsWith(`.${domain}`);
}

/**
 * Pick the media element that should play a source.
 * @param {string} src
 * @param {string} [type] MIME type recorded with the media
 * @param {string} [storage] project media storage id, used for bare ids and extensionless URLs
 * @returns {MediaKind}
 */
export function detectMediaKind(src, type = "", storage = "") {
    const value = String(src || "").trim();
    const mime = String(type || "").toLowerCase();
    const url = parseUrl(value);
    const host = url?.hostname.toLowerCase() || "";
    const path = url?.pathname.toLowerCase() || value.toLowerCase().split(/[?#]/)[0];

    if (hostMatches(host, "stream.mux.com")) return "mux";

    if (path.endsWith(".mpd") || DASH_TYPES.includes(mime)) return "dash";

    const isHlsPath = path.endsWith(".m3u8") || HLS_TYPES.includes(mime);

    if (
        (hostMatches(host, "videodelivery.net") || hostMatches(host, "cloudflarestream.com")) &&
        !isHlsPath
    ) {
        return "cloudflare";
    }

    if (
        hostMatches(host, "youtube.com") ||
        hostMatches(host, "youtu.be") ||
        hostMatches(host, "youtube-nocookie.com")
    ) {
        return "youtube";
    }
    if (hostMatches(host, "vimeo.com")) return "vimeo";

    if (isHlsPath) return "hls";

    /* URL sources are stored as video/mp4, so an extensionless manifest or
       embed id falls back to the storage the user picked. */
    if (value && STORAGE_KINDS[storage]) return STORAGE_KINDS[storage];

    return "file";
}
